// Helper to read environment variables with runtime injection support
// First check window._env (injected at runtime), then Vite, then CRA

declare global {
  interface Window {
    _env?: Record<string, string | undefined>;
  }

  // Node.js process global (for build-time environment variables)
  const process: {
    env: Record<string, string | undefined>;
  };
}

interface GetEnvOptions {
  required?: boolean;
  fallback?: string;
}

const assistantAuthAuthorityFallback = typeof import.meta !== "undefined" && import.meta.env?.DEV ? "https://localhost:7435/" : undefined;

function getEnv(name: string, { required = false, fallback }: GetEnvOptions = {}): string | undefined {
  const viteKey = `VITE_${name}`;
  const craKey = `REACT_APP_${name}`;
  const rawViteKey = name; // allow direct Vite-style names passed through env

  const isPlaceholder = (val: unknown): boolean => typeof val === "string" && /\$\{[^}]+\}/.test(val);
  const normalizeValue = (val: unknown): string | undefined => {
    if (typeof val !== "string") {
      return undefined;
    }

    const trimmed = val.trim();
    if (!trimmed || isPlaceholder(trimmed)) {
      return undefined;
    }

    return trimmed;
  };

  // Runtime injection (highest priority)
  const runtimeValRaw = typeof window !== "undefined" && window._env ? window._env[name] : undefined;
  const runtimeVal = normalizeValue(runtimeValRaw);

  // Vite
  const viteValRaw = typeof import.meta !== "undefined" && import.meta.env ? (import.meta.env[viteKey] ?? import.meta.env[rawViteKey]) : undefined;
  const viteVal = normalizeValue(viteValRaw);

  // CRA / Node
  const craValRaw =
    typeof process !== "undefined" && typeof process.env === "object" && process.env
      ? (process.env as Record<string, string | undefined>)[craKey]
      : undefined;
  const craVal = normalizeValue(craValRaw);

  const value = runtimeVal ?? viteVal ?? craVal ?? fallback;
  if (required && (value === undefined || value === null || value === "")) {
    throw new Error(`Missing required env var: ${name} (checked window._env.${name}, ${viteKey}, ${craKey})`);
  }
  return value;
}

export const ENV = {
  MSAL_CLIENT_ID: getEnv("MSAL_CLIENT_ID"),
  MSAL_REDIRECT_URI: getEnv("MSAL_REDIRECT_URI"),
  MSAL_ASSISTANT_SCOPES: getEnv("MSAL_ASSISTANT_SCOPES"),
  ASSISTANT_AUTH_AUTHORITY: getEnv("ASSISTANT_AUTH_AUTHORITY", {
    fallback: assistantAuthAuthorityFallback,
  }),
  ASSISTANT_AUTH_CLIENT_ID: getEnv("ASSISTANT_AUTH_CLIENT_ID", {
    fallback: getEnv("MSAL_CLIENT_ID"),
  }),
  ASSISTANT_AUTH_REDIRECT_URI: getEnv("ASSISTANT_AUTH_REDIRECT_URI"),
  ASSISTANT_AUTH_POST_LOGOUT_REDIRECT_URI: getEnv("ASSISTANT_AUTH_POST_LOGOUT_REDIRECT_URI"),
  // Existing GraphQL API is renamed to ASSISTANT_GRAPHQL_API_URL
  ASSISTANT_GRAPHQL_API_URL: getEnv("ASSISTANT_GRAPHQL_API_URL", {
    required: true,
  }),
  // Application Insights connection string (optional, runtime-injected from Azure)
  APPLICATIONINSIGHTS_CONNECTION_STRING: getEnv("APPLICATIONINSIGHTS_CONNECTION_STRING"),
};

export default ENV;

// Derived GraphQL endpoints.
// Always constructed by appending /graphql to the base API URL.
// This centralizes the endpoint derivation logic and ensures consistency.
const deriveEndpoint = (base: string | undefined): string | undefined => {
  if (!base) return undefined;
  return `${base.replace(/\/+$/, "")}/graphql`;
};

export const ASSISTANT_GRAPHQL_ENDPOINT = deriveEndpoint(ENV.ASSISTANT_GRAPHQL_API_URL);
